import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['select', 'trigger', 'options'];

  connect() {
    this.isMouseClick = false;
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleFocus = this.handleFocus.bind(this);
    this.boundHandleMouseDown = this.handleMouseDown.bind(this);
    this.setupSelect();
  }

  setupSelect() {
    if (
      !this.hasSelectTarget ||
      !this.hasTriggerTarget ||
      !this.hasOptionsTarget
    ) {
      console.warn('Simple select controller: Missing required targets');
      return;
    }

    this.selectTarget.style.display = 'none';

    this.initializeTriggerText();

    document.addEventListener('click', this.boundHandleClickOutside);

    this.triggerTarget.setAttribute('tabindex', '0');
    this.triggerTarget.addEventListener('keydown', this.boundHandleKeydown);
    this.triggerTarget.addEventListener('focus', this.boundHandleFocus);
    this.triggerTarget.addEventListener('mousedown', this.boundHandleMouseDown);
  }

  toggle() {
    this.element.classList.toggle('open');
    const isOpen = this.element.classList.contains('open');

    if (isOpen) {
      this.updateSelectedOption();
    } else {
      this.clearKeyboardFocus();
    }
  }

  select(event) {
    const value = event.currentTarget.dataset.value;
    const text = event.currentTarget.textContent;

    this.selectTarget.value = value;

    this.triggerTarget.textContent = text;

    if (value === '') {
      this.triggerTarget.classList.add('is-default');
    } else {
      this.triggerTarget.classList.remove('is-default');
    }

    this.element.classList.remove('open');

    this.selectTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this.selectTarget.dispatchEvent(new Event('input', { bubbles: true }));
  }

  initializeTriggerText() {
    const selectedValue = this.selectTarget.value;
    if (selectedValue) {
      const selectedOption = this.optionsTarget.querySelector(
        `[data-value="${selectedValue}"]`
      );
      if (selectedOption) {
        this.triggerTarget.textContent = selectedOption.textContent;
        if (selectedValue === '') {
          this.triggerTarget.classList.add('is-default');
        } else {
          this.triggerTarget.classList.remove('is-default');
        }
      }
    } else {
      const defaultText = this.triggerTarget.textContent || 'Select';
      this.triggerTarget.textContent = defaultText;
      this.triggerTarget.classList.add('is-default');
    }
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.element.classList.remove('open');
    }
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle();
    } else if (event.key === 'Escape') {
      this.element.classList.remove('open');
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.element.classList.contains('open')) {
        this.toggle();
      }
      this.navigateOptions(event.key);
    }
  }

  navigateOptions(direction) {
    const options = Array.from(
      this.optionsTarget.querySelectorAll('[data-value]')
    );

    if (options.length === 0) {
      return; // No options to navigate
    }

    const currentValue = this.selectTarget.value;
    const currentIndex = options.findIndex(
      (option) => option.dataset.value === currentValue
    );

    options.forEach((option) => {
      option.classList.remove('is-keyboard-focused');
    });

    let nextIndex;
    if (direction === 'ArrowDown') {
      nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
    }

    const nextOption = options[nextIndex];
    if (!nextOption) {
      return; // Safety check
    }

    nextOption.classList.add('is-keyboard-focused');

    this.selectTarget.value = nextOption.dataset.value;
    this.triggerTarget.textContent = nextOption.textContent;

    if (nextOption.dataset.value === '') {
      this.triggerTarget.classList.add('is-default');
    } else {
      this.triggerTarget.classList.remove('is-default');
    }

    this.selectTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this.selectTarget.dispatchEvent(new Event('input', { bubbles: true }));
  }

  updateSelectedOption() {
    if (!this.hasOptionsTarget) return;

    const options = this.optionsTarget.querySelectorAll('[data-value]');
    const currentValue = this.selectTarget.value;

    options.forEach((option) => {
      option.classList.remove('is-selected', 'is-keyboard-focused');
      if (option.dataset.value === currentValue) {
        option.classList.add('is-selected');
      }
    });
  }

  clearKeyboardFocus() {
    if (!this.hasOptionsTarget) return;

    const options = this.optionsTarget.querySelectorAll('[data-value]');
    options.forEach((option) => {
      option.classList.remove('is-keyboard-focused');
    });
  }

  handleFocus() {
    if (!this.isMouseClick && !this.element.classList.contains('open')) {
      this.element.classList.add('open');
      this.updateSelectedOption();
    }
    this.isMouseClick = false;
  }

  handleMouseDown() {
    this.isMouseClick = true;
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside);
    this.triggerTarget.removeEventListener('keydown', this.boundHandleKeydown);
    this.triggerTarget.removeEventListener('focus', this.boundHandleFocus);
    this.triggerTarget.removeEventListener(
      'mousedown',
      this.boundHandleMouseDown
    );
  }
}
